type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface HeartRateData {
  date: string;
  low: number;
  high: number;
  avg: number;
}

interface HourlyHeartRateData {
  datetime: string;
  hour: number;
  low: number;
  high: number;
  avg: number;
}

export const formatDate = (dateStr: string, timeRange: TimeRange) => {
  // Parse date string as local date to avoid timezone issues
  const [datePart] = dateStr.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);

  if (timeRange === "7d") {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } else if (timeRange === "1m") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } else if (timeRange === "6m") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } else if (timeRange === "1y") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatWeekday = (dateStr: string) => {
  // Parse date string as local date to avoid timezone issues
  const [datePart] = dateStr.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return date.toLocaleDateString("en-US", { weekday: "short" });
};

// Helper function to create tooltip label based on whether min and max are the same
export const createTooltipLabel = (
  timeLabel: string,
  lowValue: number,
  highValue: number,
  index: number,
  filteredData: (HeartRateData | HourlyHeartRateData)[],
  isHighPoint = false,
) => {
  const item = filteredData[index];
  if (!item)
    return `${timeLabel}\n${isHighPoint ? "High" : "Low"}: ${isHighPoint ? highValue : lowValue} bpm`;

  // Check if min and max are the same
  const isSameValue = lowValue === highValue;

  if (isSameValue) {
    return `${timeLabel}\nHigh: ${highValue} bpm\nLow: ${lowValue} bpm`;
  } else {
    // For different values, show the correct label based on whether this is a high or low point
    const value = isHighPoint ? highValue : lowValue;
    const label = isHighPoint ? "High" : "Low";
    return `${timeLabel}\n${label}: ${value} bpm`;
  }
};

// Function to generate tooltip time label with navigation offset support
export const generateTooltipTimeLabel = (
  selectedRange: TimeRange,
  index: number,
  filteredData: (HeartRateData | HourlyHeartRateData)[],
  navigationOffset: number,
): string => {
  if (selectedRange === "1d") {
    // Get the actual hour from the data
    const dataItem = filteredData[index];
    const actualHour = "hour" in dataItem ? dataItem.hour : undefined;

    if (actualHour !== undefined) {
      // Calculate the exact date and time for the specific hour
      const today = new Date();
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + navigationOffset);
      // Set to the actual hour from the data
      targetDate.setHours(actualHour, 0, 0, 0);

      const dateStr = targetDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const timeStr = targetDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: false,
      });
      return `${dateStr}\n${timeStr}`;
    } else {
      // Fallback to index if hour is not available
      const today = new Date();
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + navigationOffset);
      targetDate.setHours(index, 0, 0, 0);
      const dateStr = targetDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const timeStr = targetDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: false,
      });
      return `${dateStr}\n${timeStr}`;
    }
  } else if (filteredData[index]) {
    const item = filteredData[index];
    if ("date" in item) {
      // Parse date string as local date to avoid timezone issues
      const [datePart] = item.date.split("T");
      const [year, month, day] = datePart.split("-").map(Number);
      const date = new Date(year, month - 1, day, 12, 0, 0, 0);

      if (selectedRange === "7d") {
        return date.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } else if (selectedRange === "1m") {
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } else if (selectedRange === "6m") {
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
        });
      } else if (selectedRange === "1y") {
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
        });
      }
    }
  }
  return "";
};
